import clsx from "clsx";
import React from "react";
import { useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { ControlledDateField } from "src/ui/shared/form/DateField";

import { Box, Typography } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { RadioField, RadioFieldGorup } from "@shared/form/RadioField";
import { Tooltip } from "@shared/Tooltip";

import { RolloutFormValues, ScheduleType } from "../CreateRolloutSidePanel.utils";

import { ScheduleDateFormat } from "./schedule.validation";
import { useStyles } from "./ScheduleStep.styles";

type Props = {
    onNext: () => void;
    onBack: () => void;
};

export const ScheduleStep = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);
    const { values, errors } = useFormState<RolloutFormValues>();
    const scheduleType = values?.scheduleType;

    const isTimeWindow = scheduleType === ScheduleType.TimeWindow;
    const isNextDisabled = isTimeWindow && !!(errors?.windowStart || errors?.windowEnd);
    const tooltipMessage = (isTimeWindow && (errors?.windowStart ?? errors?.windowEnd)) || "";

    return (
        <React.Fragment>
            <Box className={classes.content}>
                <Box className={classes.root}>
                    <Typography variant="subtitle2">{t("rolloutPanel.schedule.title")}</Typography>

                    <RadioFieldGorup name="scheduleType">
                        <Box
                            className={clsx(
                                classes.cardLabelContent,
                                scheduleType === ScheduleType.WhenFound && classes.cardSelected,
                            )}
                        >
                            <RadioField value={ScheduleType.WhenFound} label={t("rolloutPanel.schedule.whenFound.label")} />

                            <Typography variant="body2" className={classes.descriptionText}>
                                {t("rolloutPanel.schedule.whenFound.description")}
                            </Typography>
                        </Box>

                        <Box
                            className={clsx(
                                classes.cardLabelContent,
                                scheduleType === ScheduleType.TimeWindow && classes.cardSelected,
                            )}
                        >
                            <RadioField value={ScheduleType.TimeWindow} label={t("rolloutPanel.schedule.timeWindow.label")} />

                            <Typography variant="body2" className={classes.descriptionText}>
                                {t("rolloutPanel.schedule.timeWindow.description")}
                            </Typography>

                            {isTimeWindow && (
                                <Box className={classes.cardDateFields}>
                                    <Box className={classes.datePickerField}>
                                        <Typography component="label" htmlFor="windowStart" variant="body2">
                                            {t("rolloutPanel.schedule.timeWindow.windowOpens")}
                                        </Typography>

                                        <ControlledDateField
                                            name="windowStart"
                                            placeholder={t("rolloutPanel.schedule.timeWindow.placeholderStart")}
                                            showTimeSelect
                                            timeIntervals={15}
                                            dateFormat={ScheduleDateFormat}
                                        />
                                    </Box>

                                    <Box className={classes.datePickerField}>
                                        <Typography component="label" htmlFor="windowEnd" variant="body2">
                                            {t("rolloutPanel.schedule.timeWindow.windowCloses")}
                                        </Typography>

                                        <ControlledDateField
                                            name="windowEnd"
                                            placeholder={t("rolloutPanel.schedule.timeWindow.placeholderEnd")}
                                            showTimeSelect
                                            timeIntervals={15}
                                            dateFormat={ScheduleDateFormat}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </RadioFieldGorup>
                </Box>
            </Box>
            <Box className={classes.footer}>
                <SecondaryButton onClick={props.onBack}>{t("common:button.back")}</SecondaryButton>
                <Tooltip title={tooltipMessage} hide={!isNextDisabled}>
                    <Box component="span" className={classes.disabledButtonWrapper}>
                        <PrimaryButton onClick={props.onNext} disabled={isNextDisabled}>
                            {t("common:button.next")}
                        </PrimaryButton>
                    </Box>
                </Tooltip>
            </Box>
        </React.Fragment>
    );
};
